import type { ReactNode } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text as RNText,
  TextInput,
  View,
  type StyleProp,
  type TextInputProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { colors, fonts, HIT, radii, tones, type Tone } from '../tokens';
import { Icon, type IconName } from './Icon';

// ── text ─────────────────────────────────────────────────────────────────
type TextVariant = 'display' | 'title' | 'heading' | 'body' | 'bodyStrong' | 'caption' | 'label';

const textVariants: Record<TextVariant, TextStyle> = {
  display: { fontFamily: fonts.display, fontSize: 28, fontWeight: '800', lineHeight: 31 },
  title: { fontFamily: fonts.display, fontSize: 22, fontWeight: '800', lineHeight: 26 },
  heading: { fontFamily: fonts.body, fontSize: 15, fontWeight: '700' },
  body: { fontFamily: fonts.body, fontSize: 14, fontWeight: '500' },
  bodyStrong: { fontFamily: fonts.body, fontSize: 14, fontWeight: '700' },
  caption: { fontFamily: fonts.body, fontSize: 12, fontWeight: '500', color: colors.textMuted },
  label: { fontFamily: fonts.body, fontSize: 11, fontWeight: '600', color: colors.textMuted },
};

export type TextProps = {
  variant?: TextVariant;
  color?: string;
  children: ReactNode;
  style?: StyleProp<TextStyle>;
  numberOfLines?: number;
  /** Tabular figures so prices and timers don't jitter. */
  numeric?: boolean;
};

export const Text = ({ variant = 'body', color, children, style, numberOfLines, numeric }: TextProps) => (
  <RNText
    numberOfLines={numberOfLines}
    style={[{ color: colors.text }, textVariants[variant], color ? { color } : null, numeric ? { fontVariant: ['tabular-nums'] } : null, style]}
  >
    {children}
  </RNText>
);

// ── buttons ──────────────────────────────────────────────────────────────
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'dark' | 'ghost';

const buttonColors: Record<ButtonVariant, { bg: string; fg: string; border: string }> = {
  primary: { bg: colors.primary, fg: colors.onPrimary, border: colors.primary },
  secondary: { bg: colors.surfaceMuted, fg: colors.text, border: colors.surfaceMuted },
  outline: { bg: colors.surface, fg: colors.text, border: colors.borderStrong },
  dark: { bg: colors.navy, fg: colors.textOnDark, border: colors.navy },
  ghost: { bg: 'transparent', fg: colors.primaryText, border: 'transparent' },
};

export type ButtonProps = {
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  icon?: IconName;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
};

export const Button = ({ label, onPress, variant = 'primary', size = 'lg', icon, disabled, loading, style, accessibilityLabel }: ButtonProps) => {
  const c = buttonColors[variant];
  const height = size === 'lg' ? 52 : HIT;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      disabled={disabled || loading}
      onPress={onPress}
      style={({ pressed }) => [
        styles.button,
        { height, borderRadius: height / 2, backgroundColor: c.bg, borderColor: c.border, opacity: disabled ? 0.5 : 1, transform: [{ scale: pressed ? 0.97 : 1 }] },
        style,
      ]}
    >
      {loading ? <ActivityIndicator color={c.fg} /> : icon ? <Icon name={icon} size={18} color={c.fg} strokeWidth={2.2} /> : null}
      <Text variant="bodyStrong" color={c.fg} style={{ fontSize: 15 }}>
        {label}
      </Text>
    </Pressable>
  );
};

export type IconButtonProps = {
  icon: IconName;
  label: string;
  onPress?: () => void;
  background?: string;
  color?: string;
  size?: number;
  badge?: number | null;
  style?: StyleProp<ViewStyle>;
};

export const IconButton = ({ icon, label, onPress, background = colors.surface, color = colors.text, size = HIT, badge, style }: IconButtonProps) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    onPress={onPress}
    style={({ pressed }) => [{ width: size, height: size, borderRadius: size / 2, backgroundColor: background, alignItems: 'center', justifyContent: 'center', transform: [{ scale: pressed ? 0.94 : 1 }] }, style]}
  >
    <Icon name={icon} size={22} color={color} strokeWidth={2.2} />
    {badge ? (
      <View style={styles.badge}>
        <Text variant="label" color="#FFFFFF" style={{ fontSize: 11, fontWeight: '700' }}>
          {String(badge)}
        </Text>
      </View>
    ) : null}
  </Pressable>
);

// ── selection ────────────────────────────────────────────────────────────
export type ChipProps = {
  label: string;
  selected?: boolean;
  onPress?: () => void;
  /** `solid` = navy fill when selected (category filter), `primary` = blue fill (options). */
  tone?: 'solid' | 'primary';
  style?: StyleProp<ViewStyle>;
  height?: number;
};

export const Chip = ({ label, selected = false, onPress, tone = 'primary', style, height = 38 }: ChipProps) => {
  const fill = tone === 'solid' ? colors.navy : colors.primary;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onPress}
      style={[
        { height, paddingHorizontal: 14, borderRadius: height / 2, borderWidth: tone === 'solid' ? 1 : 2, alignItems: 'center', justifyContent: 'center' },
        { backgroundColor: selected ? fill : colors.surface, borderColor: selected ? fill : colors.border },
        style,
      ]}
    >
      <Text variant="bodyStrong" color={selected ? (tone === 'solid' ? colors.textOnDark : colors.onPrimary) : colors.text} style={{ fontSize: 13 }}>
        {label}
      </Text>
    </Pressable>
  );
};

export type BadgeProps = { label: string; tone?: Tone; background?: string; color?: string; style?: StyleProp<ViewStyle> };

export const Badge = ({ label, tone = 'info', background, color, style }: BadgeProps) => (
  <View style={[{ alignSelf: 'flex-start', paddingHorizontal: 10, paddingVertical: 3, borderRadius: radii.pill, backgroundColor: background ?? tones[tone].bg }, style]}>
    <Text variant="label" color={color ?? tones[tone].fg} style={{ fontSize: 12, fontWeight: '700' }}>
      {label}
    </Text>
  </View>
);

export type ToggleProps = { value: boolean; onChange: (value: boolean) => void; label: string; activeColor?: string };

/** Switch drawn like the design (46×28 track) with a real accessible role. */
export const Toggle = ({ value, onChange, label, activeColor = colors.primary }: ToggleProps) => (
  <Pressable
    accessibilityRole="switch"
    accessibilityLabel={label}
    accessibilityState={{ checked: value }}
    onPress={() => onChange(!value)}
    style={{ width: 46, height: 28, borderRadius: 14, backgroundColor: value ? activeColor : colors.borderStrong, justifyContent: 'center' }}
  >
    <View style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: '#FFFFFF', marginLeft: value ? 21 : 3 }} />
  </Pressable>
);

export type CheckboxProps = { checked: boolean; onChange: (checked: boolean) => void; label: ReactNode; trailing?: ReactNode };

export const Checkbox = ({ checked, onChange, label, trailing }: CheckboxProps) => (
  <Pressable accessibilityRole="checkbox" accessibilityState={{ checked }} onPress={() => onChange(!checked)} style={styles.checkRow}>
    <View style={[styles.checkBox, { borderColor: checked ? colors.primaryText : colors.inputBorder, backgroundColor: checked ? colors.primary : colors.surface }]}>
      {checked ? <Icon name="check" size={14} color={colors.onPrimary} strokeWidth={3.5} /> : null}
    </View>
    <View style={{ flex: 1 }}>{typeof label === 'string' ? <Text>{label}</Text> : label}</View>
    {trailing}
  </Pressable>
);

export type SegmentOption<V extends string> = { value: V; label: string; icon?: IconName };

export type SegmentedProps<V extends string> = {
  options: SegmentOption<V>[];
  value: V;
  onChange: (value: V) => void;
  label: string;
  /** Track colour; the design uses translucent white on the sky header. */
  track?: string;
  style?: StyleProp<ViewStyle>;
};

/** Two-or-more way switch (delivery / pickup) drawn as a pill track with a white thumb. */
export const Segmented = <V extends string>({ options, value, onChange, label, track = 'rgba(255,255,255,.45)', style }: SegmentedProps<V>) => (
  <View accessibilityRole="radiogroup" aria-label={label} style={[{ flexDirection: 'row', backgroundColor: track, borderRadius: radii.md, padding: 4 }, style]}>
    {options.map((option) => {
      const on = option.value === value;
      return (
        <Pressable
          key={option.value}
          accessibilityRole="radio"
          accessibilityState={{ checked: on }}
          onPress={() => onChange(option.value)}
          style={{ flex: 1, height: 40, borderRadius: 11, backgroundColor: on ? colors.surface : 'transparent', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          {option.icon ? <Icon name={option.icon} size={18} color={colors.text} /> : null}
          <Text variant="body" style={{ fontWeight: on ? '700' : '600' }}>
            {option.label}
          </Text>
        </Pressable>
      );
    })}
  </View>
);

// ── layout ───────────────────────────────────────────────────────────────
export const Card = ({ children, style, padding = 14 }: { children: ReactNode; style?: StyleProp<ViewStyle>; padding?: number }) => (
  <View style={[{ backgroundColor: colors.surface, borderRadius: radii.lg, padding }, style]}>{children}</View>
);

export const Row = ({ children, gap = 8, style, align = 'center' }: { children: ReactNode; gap?: number; style?: StyleProp<ViewStyle>; align?: ViewStyle['alignItems'] }) => (
  <View style={[{ flexDirection: 'row', alignItems: align, gap }, style]}>{children}</View>
);

export const Stack = ({ children, gap = 8, style }: { children: ReactNode; gap?: number; style?: StyleProp<ViewStyle> }) => <View style={[{ gap }, style]}>{children}</View>;

export const Divider = () => <View style={{ height: 1, backgroundColor: colors.divider }} />;

export type FieldProps = TextInputProps & { label: string; error?: string | null };

export const Field = ({ label, error, style, ...input }: FieldProps) => (
  <View style={{ gap: 6, flex: 1 }}>
    <Text variant="label" style={{ fontSize: 13, color: colors.text }}>
      {label}
    </Text>
    <TextInput
      accessibilityLabel={label}
      placeholderTextColor={colors.textSubtle}
      style={[styles.input, error ? { borderColor: colors.danger } : null, style]}
      {...input}
    />
    {error ? (
      <Text variant="caption" color={colors.danger}>
        {error}
      </Text>
    ) : null}
  </View>
);

export const Stepper = ({ value, onChange, min = 1, max = 20 }: { value: number; onChange: (v: number) => void; min?: number; max?: number }) => (
  <Row gap={4} style={{ backgroundColor: colors.background, borderRadius: 26, padding: 4 }}>
    <IconButton icon="minus" label="Giảm số lượng" onPress={() => onChange(Math.max(min, value - 1))} />
    <Text variant="bodyStrong" numeric style={{ width: 28, textAlign: 'center', fontSize: 16 }}>
      {String(value)}
    </Text>
    <IconButton icon="plus" label="Tăng số lượng" onPress={() => onChange(Math.min(max, value + 1))} />
  </Row>
);

export const ProgressBar = ({ percent, color = colors.gold, track = 'rgba(255,255,255,.18)', height = 8 }: { percent: number; color?: string; track?: string; height?: number }) => (
  <View style={{ height, backgroundColor: track, borderRadius: height / 2, overflow: 'hidden' }}>
    <View style={{ width: `${Math.max(0, Math.min(100, percent))}%`, height: '100%', backgroundColor: color, borderRadius: height / 2 }} />
  </View>
);

export const Avatar = ({ initials, background = colors.sky, size = 48 }: { initials: string; background?: string; size?: number }) => (
  <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: background, alignItems: 'center', justifyContent: 'center' }}>
    <Text variant="bodyStrong" style={{ fontWeight: '800' }}>
      {initials}
    </Text>
  </View>
);

export const Spinner = ({ color = colors.primaryText }: { color?: string }) => (
  <View style={{ padding: 32, alignItems: 'center' }}>
    <ActivityIndicator color={color} />
  </View>
);

export const ErrorNote = ({ message, onRetry }: { message: string; onRetry?: () => void }) => (
  <Card style={{ margin: 16, gap: 10, alignItems: 'flex-start' }}>
    <Text variant="bodyStrong" color={colors.danger}>
      {message}
    </Text>
    {onRetry ? <Button label="Thử lại" size="md" variant="outline" onPress={onRetry} /> : null}
  </Card>
);

const styles = StyleSheet.create({
  button: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingHorizontal: 18, borderWidth: 1.5 },
  badge: {
    position: 'absolute',
    top: -2,
    right: -2,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.red,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 48, borderBottomWidth: 1, borderBottomColor: colors.divider },
  checkBox: { width: 22, height: 22, borderRadius: radii.xs, borderWidth: 2, alignItems: 'center', justifyContent: 'center' },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    borderRadius: 12,
    paddingHorizontal: 12,
    fontSize: 14,
    color: colors.text,
    backgroundColor: colors.surface,
    fontFamily: fonts.body,
  },
});
